import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import styled from 'styled-components';

import FacebookIcon from '../../svgs/FacebookIcon';
import InstagramIcon from '../../svgs/InstagramIcon';
import LinkedInIcon from '../../svgs/LinkedInIcon';
import TiktokIcon from '../../svgs/TiktokIcon';
import XIcon from '../../svgs/XIcon';
import YouTubeIcon from '../../svgs/YouTubeIcon';
import ClipPostItem from './ClipPostItem';

import type {
  ShareableImagePreviewType,
  ShareableImageType,
  VideoClip,
} from '../../types.ts/story';
import type { Dispatch, SetStateAction } from 'react';

type PostsContentProps = {
  clip?: VideoClip;
  shareable?: ShareableImagePreviewType;
  selectedDimension: string;
  hideShareButton: boolean;
  imageRef?: React.RefObject<HTMLImageElement>;
  shareableImageToggle?: Dispatch<SetStateAction<ShareableImageType | null>>;
  posterUrl?: string;
  imageSource?: Record<string, any>;
};

export const ShareModalPostsContent = observer((props: PostsContentProps) => {
  const { clip, selectedDimension, shareable } = props;
  const [expanded, setExpanded] = useState('LinkedIn');

  const onExpand = (name: string) => {
    setExpanded(name);
  };

  return (
    <PostContent>
      <ClipPostItem
        Icon={<LinkedInIcon fillColor="currentColor" />}
        dimension={selectedDimension}
        name="LinkedIn"
        clip={clip}
        shareable={shareable}
        showBorderTop={false}
        lowerLimit={70}
        upperLimit={100}
        expanded={expanded}
        onExpand={onExpand}
        hideShareButton={props.hideShareButton}
        shareableImageToggle={props.shareableImageToggle}
        posterUrl={props.posterUrl}
        imageSource={props.imageSource}
        imageRef={props.imageRef}
      />
      <ClipPostItem
        Icon={<FacebookIcon />}
        dimension={selectedDimension}
        name="Facebook"
        clip={clip}
        shareable={shareable}
        lowerLimit={40}
        upperLimit={80}
        expanded={expanded}
        onExpand={onExpand}
        hideShareButton={props.hideShareButton}
        shareableImageToggle={props.shareableImageToggle}
        posterUrl={props.posterUrl}
        imageSource={props.imageSource}
        imageRef={props.imageRef}
      />
      <ClipPostItem
        Icon={<InstagramIcon strokeColor="var(--text-secondary)" />}
        dimension={selectedDimension}
        name="Instagram"
        clip={clip}
        shareable={shareable}
        lowerLimit={40}
        upperLimit={80}
        expanded={expanded}
        onExpand={onExpand}
        hideShareButton={props.hideShareButton}
        shareableImageToggle={props.shareableImageToggle}
        posterUrl={props.posterUrl}
        imageSource={props.imageSource}
        imageRef={props.imageRef}
      />
      {clip && (
        <ClipPostItem
          Icon={<YouTubeIcon />}
          dimension={selectedDimension}
          name="YouTube"
          clip={clip}
          shareable={shareable}
          lowerLimit={100}
          upperLimit={140}
          expanded={expanded}
          onExpand={onExpand}
          hideShareButton={props.hideShareButton}
          shareableImageToggle={props.shareableImageToggle}
          posterUrl={props.posterUrl}
          imageRef={props.imageRef}
        />
      )}
      <ClipPostItem
        Icon={<XIcon />}
        dimension={selectedDimension}
        name="Twitter"
        clip={clip}
        shareable={shareable}
        showBorderBottom={!clip}
        lowerLimit={100}
        upperLimit={140}
        expanded={expanded}
        onExpand={onExpand}
        hideShareButton={props.hideShareButton}
        shareableImageToggle={props.shareableImageToggle}
        posterUrl={props.posterUrl}
        imageSource={props.imageSource}
        imageRef={props.imageRef}
      />
      {clip && (
        <ClipPostItem
          Icon={<TiktokIcon fillColor="var(--text-secondary)" />}
          dimension={selectedDimension}
          name="TikTok"
          clip={clip}
          shareable={shareable}
          showBorderBottom={true}
          lowerLimit={100}
          upperLimit={140}
          expanded={expanded}
          onExpand={onExpand}
          hideShareButton={props.hideShareButton}
          shareableImageToggle={props.shareableImageToggle}
          posterUrl={props.posterUrl}
          imageRef={props.imageRef}
        />
      )}
    </PostContent>
  );
});

const PostContent = styled.div`
  width: calc(100% - 16px);
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
  min-height: 0;
`;
